import { Icon } from 'components/Icon';
import { Gamepad, Home, LineChart, LogOut, Menu, Ticket, Users } from 'lucide-static';
import { lateinit } from 'shared/util';
import { role, setToken } from 'src/backend';
import { Component, type JSXProps } from 'engine/index';
import { Spacer } from 'components/Spacer';
import { navigate, parsePath, RouteChangeEvent, type Path } from 'engine/router';

export class Dashboard extends Component<JSXProps> {
	element: HTMLElement = lateinit;
	pageContainer: HTMLElement = lateinit;
	navItems: HTMLElement = lateinit;
	breadcrumbContainer: HTMLElement = lateinit;

	private _activeNavItem: HTMLElement | null = null;

	get activeNavItem() {
		return this._activeNavItem;
	}

	set activeNavItem(value) {
		if (value != this._activeNavItem) {
			this._activeNavItem?.removeAttribute('data-active');
			this._activeNavItem = value;
			this._activeNavItem?.setAttribute('data-active', '');
		}
	}

	override render(): HTMLElement {
		const toggleNav = () => {
			const fromW = navbar.clientWidth;
			navbar.toggleAttribute('data-expanded');
			localStorage.setItem('nav-expanded', '' + navbar.hasAttribute('data-expanded'));
			const toW = navbar.clientWidth;

			navbar.animate([{ width: fromW + 'px' }, { width: toW + 'px' }], {
				easing: 'ease-in-out',
				duration: 80,
			});
		};

		const startExpanded = localStorage.getItem('nav-expanded') === 'true';

		const view: HTMLElement = (
			<div class="flex h-full bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300">
				<div
					nav-bar
					data-expanded={startExpanded ? true : undefined}
					class="group/nav flex h-full flex-col rounded-r-xl bg-white p-2 shadow-lg data-[expanded]:w-[300px] dark:bg-gray-800"
				>
					<button
						on:click={toggleNav}
						class="mb-3 mr-auto flex justify-center rounded-md p-2 text-gray-900 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-700"
					>
						<Icon class="pointer-events-none size-5" icon={Menu} />
					</button>

					{/* <div class="w-full rounded-md bg-gray-200 p-3 text-center text-xl text-gray-500">logo</div> */}
					<div nav-items class="flex flex-1 shrink flex-col">
						<NavItem name="Dashboard" href="/dashboard" icon={Home} />
						{role == 'admin' && <NavItem name="Games" href="/dashboard/games" icon={Gamepad} />}
						<NavItem name="Licenses" href="/dashboard/licenses" icon={Ticket} />
						{role == 'admin' && <NavItem name="Users" href="/dashboard/users" icon={Users} />}
						{role == 'admin' && <NavItem name="Statistics" href="/dashboard/statistics" icon={LineChart} />}

						{/* <NavItem name="Settings" href="/dashboard/settings" icon={Settings} /> */}
						<Spacer />
						<NavItem
							name="Sign Out"
							icon={LogOut}
							onclick={() => {
								setToken(undefined);
								navigate('/login');
							}}
						/>
					</div>
				</div>
				<div class="flex h-full flex-1 shrink flex-col gap-8 overflow-auto p-8">
					<div page-breadcrumbs class="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400"></div>
					<div class="flex-1 shrink" page-container></div>
				</div>
			</div>
		);

		this.element = view;
		const navbar = view.querySelector('[nav-bar]') as HTMLElement;
		this.navItems = view.querySelector('[nav-items]') as HTMLElement;
		this.pageContainer = view.querySelector('[page-container]') as HTMLElement;
		this.breadcrumbContainer = view.querySelector('[page-breadcrumbs]') as HTMLElement;

		// handle link clicks
		view.addEventListener('click', (e) => {
			if (e.target && e.target instanceof HTMLElement) {
				const link = e.target.hasAttribute('data-href') ? e.target : e.target.closest('[data-href]');
				if (link) {
					e.preventDefault();
					const href = link.getAttribute('data-href') as string;
					// @ts-expect-error it's fine
					navigate(href);
				}
			}
		});

		// @ts-expect-error custom event
		window.addEventListener('routechange', (e: RouteChangeEvent) => {
			if (e.path && e.path[0] === 'dashboard') {
				const pageId = e.path[1] ?? 'dashboard';
				this.activeNavItem = this.navItems.querySelector(`[data-page=${pageId}]`);
			}
		});

		return view;
	}
}

type NavItemProps = {
	icon: string;
	name: string;
	active?: boolean;
	href?: Path;
	onclick?: () => void;
};

function NavItem({ active, icon, name, onclick, href }: NavItemProps) {
	return (
		<a
			data-active={active ?? undefined}
			data-page={parsePath(href ?? '/dashboard')[1] ?? 'dashboard'}
			href={href}
			on:click={onclick}
			tabindex="0"
			role="link"
			class="group relative flex cursor-pointer items-center gap-2 rounded-md border-t border-transparent p-2 text-sm text-gray-800 shadow-theme-600 transition-all hover:bg-gray-100 data-[active]:border-theme-100 data-[active]:bg-theme-600 data-[active]:text-theme-50 data-[active]:shadow-sm group-data-[expanded]/nav:px-3 dark:text-gray-300 dark:shadow-theme-500/70 dark:hover:bg-gray-700 dark:data-[active]:border-theme-500/30 dark:data-[active]:bg-theme-600 dark:data-[active]:text-theme-300"
			// prevent weird ghost url when accidentally dragging
			on:dragstart={(e: DragEvent) => e.preventDefault()}
		>
			<Icon
				icon={icon}
				class="pointer-events-none size-5 shrink-0 p-[2px] text-gray-500 group-data-[active]:text-theme-100 dark:text-gray-400 dark:group-data-[active]:text-theme-400"
			/>
			<span class="hidden w-0 shrink overflow-hidden whitespace-nowrap group-data-[expanded]/nav:block group-data-[expanded]/nav:w-auto">
				{name}
			</span>
			<span class="before:bg-inherit pointer-events-none absolute -right-4 translate-x-full whitespace-nowrap rounded-md border border-gray-200 bg-white p-2 leading-none text-gray-800 opacity-0 drop-shadow-sm before:absolute before:left-0 before:top-1/2 before:block before:size-2 before:-translate-x-1/2 before:-translate-y-1/2 before:rotate-45 before:rounded-sm before:border before:border-gray-200 before:bg-white before:clip-half group-hover:opacity-100 group-data-[expanded]/nav:hidden dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100 dark:before:border-gray-700 dark:before:bg-gray-950">
				{name}
			</span>
		</a>
	);
}
